/**
 * Phone Number Formatting Utilities
 * Standardizes phone numbers to US format: (XXX) XXX-XXXX
 */

/**
 * Strips all non-numeric characters from phone number
 * @param {string} phone - Raw phone number input
 * @returns {string} - Numbers only
 */
export const stripNonNumeric = (phone) => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

/**
 * Validates that phone number has exactly 10 digits (US format)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid 10-digit number
 */
export const isValidUSPhone = (phone) => {
  const numeric = stripNonNumeric(phone);
  return numeric.length === 10;
};

/**
 * Formats phone number to standard US format: (XXX) XXX-XXXX
 * @param {string} phone - Raw phone number input
 * @returns {string} - Formatted phone number or original if invalid
 */
export const formatUSPhone = (phone) => {
  if (!phone) return '';
  
  const numeric = stripNonNumeric(phone);
  
  // If not exactly 10 digits, return original (let validation handle it)
  if (numeric.length !== 10) {
    return phone;
  }
  
  // Format as (XXX) XXX-XXXX
  const areaCode = numeric.slice(0, 3);
  const prefix = numeric.slice(3, 6);
  const lineNumber = numeric.slice(6, 10);
  
  return `(${areaCode}) ${prefix}-${lineNumber}`;
};

/**
 * Normalizes phone number for storage (removes formatting)
 * @param {string} phone - Formatted or unformatted phone number
 * @returns {string} - Numeric only version for storage
 */
export const normalizePhoneForStorage = (phone) => {
  return stripNonNumeric(phone);
};

/**
 * Validates and formats phone number for display
 * Returns error message if invalid, formatted phone if valid
 * @param {string} phone - Raw phone input
 * @returns {object} - { valid: boolean, formatted: string, error: string }
 */
export const validateAndFormatPhone = (phone) => {
  if (!phone || phone.trim() === '') {
    return {
      valid: false,
      formatted: '',
      error: ''
    };
  }
  
  const numeric = stripNonNumeric(phone);
  
  if (numeric.length === 0) {
    return {
      valid: false,
      formatted: '',
      error: 'Please enter a valid phone number.'
    };
  }
  
  if (numeric.length !== 10) {
    return {
      valid: false,
      formatted: phone,
      error: 'Please enter a valid 10-digit phone number (numbers only).'
    };
  }
  
  return {
    valid: true,
    formatted: formatUSPhone(numeric),
    error: ''
  };
};
